import {
  TAlertProps,
  TPopupInitialState,
  TPopupList,
  TPopupOpenProps,
} from '@@types/popup';
import {
  closeAllPopup,
  closePopup,
  closePopupById,
  closePopupByQueryId,
  openAlert,
  openPopup,
  popupSelector,
} from '@stores/popup';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';

const CheckoutContainer = dynamic(() => import('@containers/checkout'), {
  ssr: false,
});
const AddressControlModal = dynamic(
  () => import('@containers/address/popups/address-control.modal'),
  { ssr: false },
);
const RestaurantMenuModal = dynamic(
  () => import('@containers/restaurants/popups/restaurant-menu.modal'),
  { ssr: false },
);

export const POPUP_CONTENTS = {
  cart: CheckoutContainer,
  address: AddressControlModal,
  restaurantMenu: RestaurantMenuModal,
};

export type TPopupComponentKeys = keyof typeof POPUP_CONTENTS;

export default function usePopup() {
  const dispatch = useDispatch();
  const router = useRouter();
  const popups = useSelector(popupSelector);

  const handleOpenPopup = <T extends TPopupList>(
    { id, type, props, Component, popupOptions }: TPopupInitialState<T>,
    options?: { isBackClose?: boolean; queryId?: TPopupComponentKeys },
  ) => {
    if (id && !!popups.some((popup) => popup.id === id)) {
      return;
    }
    const queryId = options?.queryId;
    const isBackClose = options?.isBackClose;

    dispatch(
      openPopup({ type, props, Component, popupOptions, queryId, isBackClose }),
    );
    if (queryId) {
      const pids = router.query?.pid;

      sessionStorage.setItem(
        `__scroll_${window.history.state.key}`,
        JSON.stringify({ x: window.scrollX, y: window.scrollY }),
      );

      if (!pids) {
        const pidData = `${queryId}@${JSON.stringify({
          type,
          props,
          popupOptions,
        })}`;
        router.push(
          {
            pathname: router.pathname,
            // query: { pid: pidData },
            query: { ...router.query, pid: pidData },
          },
          undefined,
          { shallow: true },
        );
      }
    }
  };

  const handleClosePopup = async (queryId?: TPopupComponentKeys) => {
    if (queryId) {
      const { pid, ...otherQuery } = router.query;
      if (!pid) return;

      if (typeof pid === 'string') {
        await router.replace(
          { pathname: router.pathname, query: otherQuery },
          undefined,
          { shallow: true, unstable_skipClientCache: true },
        );
      } else {
        const splitedPids = pid.map((_pid) => _pid.split('@')[0]);

        const targetPid = splitedPids.filter((_pid) =>
          Array.isArray(queryId)
            ? queryId.includes(_pid as TPopupComponentKeys)
            : _pid === queryId,
        );

        if (!targetPid) return;
        const filteredPid = pid.filter((_pid) =>
          targetPid.includes(_pid.split('@')[0]),
        );
        await router.replace(
          {
            pathname: router.pathname,
            query: { pid: filteredPid, ...otherQuery },
          },
          undefined,
          { shallow: true, scroll: true },
        );
      }
    }
    dispatch(closePopup({}));
  };

  const handlePromiseOpenPopup = <T extends TPopupList, K>(
    openProps: TPopupOpenProps<T>,
  ) => {
    return new Promise<K>((resolve: (params: K) => void) => {
      dispatch(
        openPopup({
          ...openProps,
          props: {
            ...openProps.props,
            resolve,
          },
        }),
      );
    });
  };

  const handleOpenAlert = <T>(
    alertProps: Omit<TAlertProps, 'resolve'>,
    options?: { isBackClose?: boolean },
  ) => {
    return new Promise<T>((resolve: (params: T) => void) => {
      const isBackClose = options?.isBackClose;
      dispatch(
        openAlert({
          alertOptions: { ...alertProps, isBackClose, resolve },
        }),
      );
    });
  };

  const handleClosePopupByQueryId = async (
    queryId: TPopupComponentKeys | TPopupComponentKeys[],
  ) => {
    dispatch(closePopupByQueryId(queryId));
  };

  const handleClosePopupById = (id: string) => {
    dispatch(closePopupById(id));
  };

  const handleAllClose = () => {
    dispatch(closeAllPopup({}));
  };

  return {
    openPopup: handleOpenPopup,
    closePopup: handleClosePopup,
    openPromisePopup: handlePromiseOpenPopup,
    openAlert: handleOpenAlert,
    closePopupByQueryId: handleClosePopupByQueryId,
    closePopupById: handleClosePopupById,
    closeAllPopup: handleAllClose,
  };
}
